import { formatNumber } from 'utils/formatter';
import { convertPercentage } from 'components/features/utils/math';
import { convertNumber, getToday } from 'utils/date';
import AvatarIcon, { avatarIcon } from 'components/features/utils/AvatarIcon';
import React from 'react';
import { TimeOverComment } from './TimeOverComment';
import { AchievedComment } from './AchievedComment';
import { TitleComment } from './TitleComment';
import { YieldComment } from './YieldComment';

export const AllAICheer = ({
  achieved,
  title,
  currentAmount,
  targetAmount,
  benchmarkAmount,
  targetStartDate,
  targetEndDate,
  requiredYield,
  goHowToIncreaseAccumulationAmount,
}: {
  achieved: boolean;
  title: string;
  currentAmount: number;
  targetAmount: number;
  benchmarkAmount: number;
  targetStartDate: Date;
  targetEndDate: Date;
  requiredYield: number | null;
  goHowToIncreaseAccumulationAmount: () => void;
}): React.ReactElement => {
  const deviationRate = convertPercentage(
    currentAmount / targetAmount - benchmarkAmount / targetAmount || 0,
    1
  );
  const timeOver = convertNumber(targetEndDate) < convertNumber(getToday());
  const advice: {
    icon: avatarIcon;
    title: string;
    detail: string;
  } = (() => {
    if (achieved) {
      return {
        icon: 'veryGood' as avatarIcon,
        title: '',
        detail: '',
      };
    }
    if (deviationRate < -20) {
      return {
        icon: 'veryBad' as avatarIcon,
        title: '運用方針を見直すこともご検討ください',
        detail: `「${title}」の最終目標額に対する進捗はベンチマーク（目標値）より${formatNumber(
          Math.abs(deviationRate),
          { numberOfDecimal: 1 }
        )}％遅れています。利回りの高い運用ができる資産へ組み替えるなど、保有資産を見直してみることもご検討ください。もしくは、目標金額や目標時期の見直しをすることで無理なく続けることができます。`,
      };
    }
    if (deviationRate < 0) {
      return {
        icon: 'bad' as avatarIcon,
        title: '着実に進めていくことが成功のカギ',
        detail: `「${title}」の最終目標額に対する進捗はベンチマーク（目標値）より${formatNumber(
          Math.abs(deviationRate),
          { numberOfDecimal: 1 }
        )}％遅れています。諦めずに続けることが成功のカギです。しっかり続けていきましょう。`,
      };
    }
    if (deviationRate < 20) {
      return {
        icon: 'average' as avatarIcon,
        title: '計画より進んでいます！順調ですね',
        detail: `「${title}」の最終目標額に対する進捗はベンチマーク（目標値）より${formatNumber(
          deviationRate,
          { numberOfDecimal: 1 }
        )}％進んでいます。順調ですね！この調子で続けていきましょう。`,
      };
    }
    return {
      icon: 'good' as avatarIcon,
      title: '計画よりかなり進んでいます！絶好調ですね',
      detail: `「${title}」の最終目標額に対する進捗はベンチマーク（目標値）より${formatNumber(
        deviationRate,
        { numberOfDecimal: 1 }
      )}％も進んでいます。かなり良いペースです！この調子で続けていきましょう。`,
    };
  })();

  return (
    <>
      {achieved ? (
        <AchievedComment title={title} />
      ) : (
        <>
          {timeOver ? (
            <TimeOverComment icon={advice.icon} />
          ) : (
            <>
              <div className="w-full flex flex-col">
                <div className="flex flex-row">
                  <AvatarIcon icon={advice.icon} />
                  <TitleComment
                    className={'ml-4'}
                    targetEndDate={targetEndDate}
                    targetStartDate={targetStartDate}
                    currentAmount={currentAmount}
                    targetAmount={targetAmount}
                  />
                </div>
                <div className="flex flex-col">
                  <div className="mt-2">
                    <div className="text-bluegray-700 text-sm font-bold">
                      {advice.title}
                    </div>
                    <div className="text-bluegray-700 text-sm mt-1">
                      {advice.detail}
                    </div>
                  </div>
                  <YieldComment
                    className={'mt-2'}
                    title={title}
                    requiredYield={requiredYield}
                    goHowToIncreaseAccumulationAmount={
                      goHowToIncreaseAccumulationAmount
                    }
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};
